import * as R from 'ramda'
import {
  doesBenefitExist,
  extractBenefitsData,
} from 'sidecar-member-components/utils'
import { getMaternityAllowanceOptionsWithYear } from 'utils/packages/coveragePackageUtils'
import { calculateStartDate } from 'utils/flows'

/**
 * Takes applicant entities and sifts out the primary and the dependents to be
 * stored separately.
 *
 * @param {obj} state A reducer state object
 */
export const siftApplicants = state => {
  const entities = R.pathOr({}, ['applicants', 'entities'], state)
  const applicantsArray = Object.values(entities)
  const primaryApplicant = applicantsArray.find(p => p && p.primary) || {}
  const dependents = applicantsArray.filter(p => p && !p.primary) || []

  return {
    ...state,
    applicants: {
      ...R.pathOr({}, ['applicants'], state),
      primaryApplicant,
      dependents,
    },
  }
}
/**
 * Cleans an applicant by removing the temporary UUID that we assigned to the
 * uuid field when the applicant was created. The backend will throw an error if
 * we send a temp uuid to them, but they've made a new field we can use called
 * `tempUuid`. It'll be ignored and passed back to us in the response. This can
 * be used to associate the payload contents with their corresponding
 * applicant.
 *
 * @param {obj} applicant The applicant to clean
 * @param {obj} config A map of config settings to specify how to clean
 */
export const cleanTempApplicant = (applicant, config = {}) => {
  const cleanApplicant = { ...applicant }

  // move the temporary uuid into a tempUuid field
  // to be ignored by the backend. it will be returned.
  if (/:/.test(cleanApplicant.uuid)) {
    cleanApplicant.tempUuid = cleanApplicant.uuid
    delete cleanApplicant.uuid
  }

  // some endpoints like their applicants with the start date in the root
  // this also means they don't give a shit about the quote.
  if (config.raiseStartDate) {
    // Gives a new date if there is no existing quote startDate otherwise use quote startDate
    cleanApplicant.startDate = R.pathOr(
      new Date().toISOString(),
      ['quote', 'startDate'],
      applicant,
    )
    delete cleanApplicant.quote
  }

  return cleanApplicant
}
// array-compatible version
export const cleanTempApplicants = (applicants, config) => {
  return applicants.map(applicant => cleanTempApplicant(applicant, config))
}

/**
 * Determines if the applicant is pregnant by looking at their medical history
 * answers.
 *
 * @param applicant The applicant to check
 * @param condition The condition to check for
 */
export const hasCondition = (applicant, condition) =>
  R.pipe(
    R.pathOr([], ['medicalHistory', 'answers']),
    R.find(R.propEq('uuid', condition)),
    p => !!p,
  )(applicant)

/**
 * We need to display the price difference for a couple of benefits so we get
 * estimates to see how much adding those benefits would cost then use the
 * estimate cost and the current quote cost to get the diff.
 *
 * @param {arr} applicants The list of applicants to fetch for
 * @param {fun} fetchInitialEstimate The endpoint to call to get the estimate
 * @param {str} funnelType The name for the flow we're in (for reporting).
 */
export const fetchInitialEstimates = async (
  applicants,
  fetchBenefitEstimate,
  funnelType,
) => {
  const fetches = []

  const coveredApplicants = applicants.filter(
    a => a.enrollmentStatus !== 'NOT_COVERED',
  )

  // using `for` because await only works in for loops
  for (let a in coveredApplicants) {
    const applicant = coveredApplicants[a]
    const quote = applicant.quote
    const disabledBenefits = quote.disabledBenefits
    const isPregnant = hasCondition(applicant, 'pregnant')

    const hasRateLockEnabled = !!quote.threeYearRateLock
    const hasMaternityEnabled = !!quote.maternityCovered

    // if the maternity benefit is not disabled, and the user is not
    // pregnant, fetch maternity estimates. We don't estimate for preggos
    // because the cost is scary high.
    if (!disabledBenefits.includes('maternity') && !isPregnant) {
      fetches.push(
        await fetchBenefitEstimate(
          'maternity',
          applicant,
          {
            ...(!hasMaternityEnabled
              ? getDefaultBenefitQuote('maternity', quote)
              : getDisableBenefitQuote('maternity', quote)),
          },
          funnelType,
        ),
      )
    }

    // if the rate lock benefit is not disabled and we don't already
    // have maternity coverage, fetch estimates for rate lock. Rate lock
    // is included with maternity.
    if (
      !disabledBenefits.includes('threeYearRateLock') &&
      !hasMaternityEnabled
    ) {
      fetches.push(
        await fetchBenefitEstimate(
          'threeYearRateLock',
          applicant,
          {
            ...(!hasRateLockEnabled
              ? getDefaultBenefitQuote('threeYearRateLock', quote)
              : getDisableBenefitQuote('threeYearRateLock', quote)),
          },
          funnelType,
        ),
      )
    }

    // in reapply flows we want to make sure we add the estimate for
    // rate lock to simulate a member being able to add rate lock to a
    // new policy after they've elected to remove their previously
    // enabled maternity coverage from their policy, this situation
    // is unique to reapply flow and therefore warrants its own condition
    if (
      !disabledBenefits.includes('threeYearRateLock') &&
      hasMaternityEnabled &&
      R.equals('reapply', funnelType)
    ) {
      fetches.push(
        await fetchBenefitEstimate(
          'threeYearRateLock',
          applicant,
          {
            ...getDefaultBenefitQuote('threeYearRateLock', {
              ...quote,
              maternityCovered: false,
              maternityAllowance: 0,
              maternityDeductible: 0,
            }),
          },
          funnelType,
        ),
      )
    }
  }
  await Promise.all(fetches)
}

/**
 * Takes a quote object and sets up the flags so that the benefit is enabled
 * with default values. Useful for when we need an initial estimate, or what we
 * need to reset.
 *
 * @param {str} benefit The benefit type
 * @param {obj} quote The rest of the quote object to use
 */
export const getDefaultBenefitQuote = (benefit, quote = {}) => {
  const { disabledBenefits = [] } = quote
  const maternityAllowanceOptions = getMaternityAllowanceOptionsWithYear(
    quote.insuranceFilingUuid,
    quote.maternityAllowance,
  )

  // If the $50,000 option is available, use that,
  // otherwise use the next closest one, $25,000
  const maternityAllowance = R.pipe(
    R.find(R.propEq('value', 50000)),
    R.propOr(25000, 'value'),
  )(maternityAllowanceOptions)

  switch (benefit) {
    case 'maternity':
      return {
        ...quote,
        maternityCovered: true,
        maternityAllowance,
        maternityDeductible: 0,
        // as long as threeYearRateLock isn't disabled, add threeYearRateLock
        ...(!disabledBenefits.includes('threeYearRateLock')
          ? { threeYearRateLock: true }
          : {}),
      }
    case 'threeYearRateLock':
      return {
        ...quote,
        threeYearRateLock: true,
      }
    // no default
  }
}

/**
 * Takes a quote object and disables flags and values for the benefit so that we
 * can get an estimate with that benefit turned off.
 *
 * @param {str} benefit The benefit type
 * @param {obj} quote The rest of the quote object to use
 */
export const getDisableBenefitQuote = (benefit, quote) => {
  switch (benefit) {
    case 'maternity':
      return {
        ...quote,
        threeYearRateLock: false,
        maternityCovered: false,
        maternityAllowance: 0,
        maternityDeductible: 0,
      }
    case 'threeYearRateLock':
      return {
        ...quote,
        threeYearRateLock: false,
      }
    // no default
  }
}

/**
 * Converts members to applicants
 *
 * @param member A mini or full member model
 * @returns Applicant based on the Applicant model
 */
export const convertMemberToApplicant = (
  member = {},
  { convertFiling = false, useExpiration = false } = {},
) => {
  const necessaryFields = R.pick(
    [
      'uuid',
      'firstName',
      'lastName',
      'gender',
      'dateOfBirth',
      'email',
      'primary',
      'enrollmentStatus',
      'group',
      'mailingAddress',
      'phoneNumber',
      'ssnLast4',
    ],
    member,
  )
  necessaryFields.relationWithPrimary = member.primary ? 'PRIMARY' : 'DEPENDENT'

  const benefitInfo = extractBenefitsData(member.policy)
  const getLimit = field => R.path([field, 'limitValue'], benefitInfo)
  const getBool = field => R.pathOr(false, [field, 'unlimited'], benefitInfo)
  const benefits = {
    maxAnnualAllowance: getLimit('ALLOWANCE'),
    deductible: getLimit('DEDUCTIBLE'),
    maternityAllowance: getLimit('MATERNITY_ALLOWANCE'),
    maternityDeductible: getLimit('MATERNITY_DEDUCTIBLE'),
    // these bools won't have a limit value, but if they exist at all
    // then that means they are enabled in the member's policy. They'll
    // also have "unlimited" set to true always so we can just use that.
    prescriptionsCovered: getBool('PRESCRIPTIONS_COVERED'),
    threeYearRateLock: getBool('THREE_YEAR_RATE_LOCK'),
    maternityCovered: getBool('MATERNITY_COVERED'),
  }

  // we need to have a valid quote with stateDate information
  // for the cleanTempApplicant above
  const getPolicyProp = prop => R.path(['policy', prop], member)
  const quote = {
    startDate: getPolicyProp(useExpiration ? 'expirationDate' : 'startDate'),
    ...benefits,
  }
  if (convertFiling) {
    quote.insuranceFilingUuid = getPolicyProp('insuranceFilingUuid')
  }

  const applicant = {
    ...necessaryFields,
    quote,
  }

  // If a member has a policy, run the logic to determine
  // if they have an active maternity coverage

  return R.either(R.isNil, R.isEmpty)(member.policy)
    ? applicant
    : {
        ...applicant,
        hasActiveMaternityCoverage: memberHasActiveMaternityCoverage(member),
      }
}
// calls above function for array of members
export const convertMembersToApplicants = members => {
  return members.map(member => convertMemberToApplicant(member))
}

// This checks to see if member has active maternity coverage
// This is important to make sure to remove pregnancy medical history
// condition in validate, estimate and changeCoverage

const memberHasActiveMaternityCoverage = (member = {}) => {
  const { policy = {} } = member
  const { status, coverageStatus } = policy
  const hasMaternityCovered = doesBenefitExist(
    member.policy,
    'MATERNITY_ALLOWANCE',
  )
  return (
    ['PENDING_CANCELLATION', 'ENROLLED'].includes(status) &&
    coverageStatus === 'ACTIVE' &&
    hasMaternityCovered
  )
}

// This is a good function to use if anything needs to be done
// to an applicant before being used in the flows. The motivating use-case
// was filtering out pregnancy if the member had active maternity coverage,
// without affecting the user's ability to add the pregnancy to the medical history
// in the first place

export const preprocessApplicant = (applicant = {}) => {
  // This value is set when converting member to applicant
  const { hasActiveMaternityCoverage } = applicant

  // we do this to remove duplicates in the medical history answers,
  // there is test coverage around this if you want to approach it in a different way
  const medicalHistory = removeDuplicateMedicalHistoryAnswers(
    applicant.medicalHistory,
  )

  // If there are no medicalHistory answers, return early, there is nothing
  // left to do in this function

  if (R.either(R.isNil, R.isEmpty)(medicalHistory.answers)) {
    return applicant
  }

  // If this applicant has active maternity coverage, filter out pregnancy condition
  const answers = hasActiveMaternityCoverage
    ? R.propOr([], 'answers', medicalHistory).filter(
        ({ uuid }) => uuid !== 'pregnant',
      )
    : medicalHistory.answers

  // set this new answers in the applicant
  return R.assocPath(['medicalHistory', 'answers'], answers, applicant)
}

const removeDuplicateMedicalHistoryAnswers = (medicalHistory = {}) => {
  const { answers = [] } = medicalHistory
  const uniqueAnswers = Object.values(
    answers.reduce((acc, answer) => ({ ...acc, [answer.uuid]: answer }), {}),
  )
  return {
    ...medicalHistory,
    answers: uniqueAnswers,
  }
}

/**
 * Takes covered applicants and returns the cumulative calculated premiums
 *
 * @param array CoveredApplicants
 */
export const getApplicantsTotalPremium = (coveredApplicants = []) => {
  const paymentSchedule = R.pipe(
    R.head,
    R.prop('paymentSchedule'),
  )(coveredApplicants)

  return R.pipe(
    R.pluck('quote'),
    R.pluck('paymentOptions'),
    R.unnest,
    R.filter(R.propEq('paymentSchedule', paymentSchedule)),
    R.pluck('cost'),
    R.sum,
  )(coveredApplicants)
}

/**
 * Takes covered applicants and returns the "Today's total cost" value
 *
 * @param array CoveredApplicants
 */
export const getApplicantsTotalCost = (coveredApplicants = []) => {
  const applicantsTotalPremiums = getApplicantsTotalPremium(coveredApplicants)
  const oneTimeFee = R.pipe(
    R.pluck('quote'),
    R.pluck('oneTimeFee'),
    R.sum,
  )(coveredApplicants)

  return applicantsTotalPremiums + oneTimeFee
}

export const cleanQuote = (applicants, isOffexchange) => {
  // in all offExchange flows, we will REset the startDate
  // based on a call to the BE. it's enough to just set it
  // to null here
  applicants.forEach(applicant => {
    applicant.quote = {
      startDate: isOffexchange ? null : calculateStartDate().toISOString(),
    }
  })
}

export const getACAFallbackDate = date => {
  return date.getMonth() === 11
    ? // if it's December, you need to bump the year and set month to 0
      new Date(date.getFullYear() + 1, 0, 1)
    : // otherwise, just bump the month and set the day to 1
      new Date(date.getFullYear(), date.getMonth() + 1, 1)
}

export const cleanMedicalHistory = applicants => {
  // we remove the medical history of all the applicants and give them a clean object.
  // this is done so the initializeMedicalhistory function doesn't grab the data stored in the database

  R.pipe(
    R.forEach(applicant => {
      if (applicant.medicalHistory) {
        const cleanedHistory = R.map(R.empty, applicant.medicalHistory)
        applicant.medicalHistory = cleanedHistory
        return cleanedHistory
      }
      return
    }),
  )(applicants)

  return applicants
}
